"use client"

import { useState, useEffect, useRef } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import Image from "next/image"
import { useLocale, useTranslations } from "next-intl"
import { localeMetadata, routing, type AppLocale } from "@/i18n/routing"
import SwipeGesture from "@/utils/swipe-gesture"

interface DynamicCarouselProps {
  images: string[]
  aspectRatio?: string
  autoScrollIntervalMs?: number
  showArrows?: boolean
  showDots?: boolean
  className?: string
  imageClassName?: string
  alt?: string
}

export function DynamicCarousel({
  images,
  aspectRatio = "aspect-3/1",
  autoScrollIntervalMs = 5000,
  showArrows = true,
  showDots = true,
  className = "",
  imageClassName = "",
  alt = "Carousel image"
}: DynamicCarouselProps) {
  const locale = useLocale()
  const tCarousel = useTranslations("common.carousel")
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const swipeGesture = useRef(new SwipeGesture(50))
  const validImages = images.filter(Boolean)
  const currentLocale =
    localeMetadata[locale as AppLocale] ??
    localeMetadata[routing.defaultLocale as AppLocale]

  // Parse aspect ratio for CSS
  const parseAspectRatio = () => {
    const aspectMatch = aspectRatio.match(/aspect-(\d+)\/(\d+)/)
    if (aspectMatch) {
      const [, w, h] = aspectMatch
      return `${w} / ${h}`
    }
    return '3 / 1' // default
  }

  const cssAspectRatio = parseAspectRatio()

  useEffect(() => {
    if (validImages.length > 1 && autoScrollIntervalMs > 0 && !isHovered) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % validImages.length)
      }, autoScrollIntervalMs)
      return () => clearInterval(interval)
    }
  }, [validImages.length, autoScrollIntervalMs, isHovered])

  const handleMouseEnter = () => {
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  const handleImageClick = () => {
    if (swipeGesture.current.consumeDidSwipe()) {
      return
    }
    nextImage()
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % validImages.length)
  }

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + validImages.length) % validImages.length)
  }

  const goToImage = (index: number) => {
    setCurrentImageIndex(index)
  }

  // If no images, return null
  if (validImages.length === 0) {
    return null
  }

  // If only one image, show it without carousel controls
  if (validImages.length === 1) {
    return (
      <div className={`relative w-full ${className}`}>
        <div 
          className="relative w-full rounded-lg overflow-hidden shadow-lg"
          style={{ aspectRatio: cssAspectRatio }}
        >
          <Image
            src={validImages[0]}
            alt={alt}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 100vw"
            className={`object-cover ${imageClassName}`}
            priority
          />
        </div>
      </div>
    )
  }

  return (
    <div 
      className={`relative w-full group ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div 
        className="relative w-full rounded-lg overflow-hidden shadow-lg bg-gray-50 touch-pan-y"
        style={{ aspectRatio: cssAspectRatio }}
        onTouchStart={(e) => {
          const touch = e.touches[0]
          if (!touch) return

          swipeGesture.current.start(touch.clientX, touch.clientY)
        }}
        onTouchMove={(e) => {
          const touch = e.touches[0]
          if (!touch) return

          swipeGesture.current.move(touch.clientX, touch.clientY)
        }}
        onTouchEnd={() => {
          const direction = swipeGesture.current.end()
          if (direction === "left") {
            nextImage()
          }

          if (direction === "right") {
            prevImage()
          }
        }}
        onTouchCancel={() => {
          swipeGesture.current.end()
        }}
      >
        {/* Render all images, but control visibility with opacity */}
        {validImages.map((image, index) => {
          const isActive = index === currentImageIndex;
          return (
            <div
              key={`carousel-image-${index}`}
              className="absolute inset-0"
              style={{
                opacity: isActive ? 1 : 0,
                transition: 'opacity 500ms ease-in-out',
                zIndex: isActive ? 1 : 0,
                pointerEvents: isActive ? 'auto' : 'none',
              }}
            >
              <Image
                src={image}
                alt={`${alt} ${index + 1}`}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 100vw, 100vw"
                className={`object-cover cursor-pointer ${imageClassName}`}
                priority={index === 0}
                onClick={handleImageClick}
                unoptimized={false}
              />
            </div>
          );
        })}
        
        {/* Navigation arrows */}
        {showArrows && validImages.length > 1 && (
          <>
            <button
              type="button"
              onClick={prevImage}
              className="absolute start-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow-lg transition-all opacity-0 z-20 touch-manipulation group-hover:opacity-100 group-active:opacity-100 hover:bg-white active:bg-white sm:p-3"
              aria-label={tCarousel("previous")}
            >
              {currentLocale.isRtl ? (
                <ChevronRight className="h-5 w-5 text-gray-800 sm:h-6 sm:w-6" />
              ) : (
                <ChevronLeft className="h-5 w-5 text-gray-800 sm:h-6 sm:w-6" />
              )}
            </button>
            <button
              type="button"
              onClick={nextImage}
              className="absolute end-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow-lg transition-all opacity-0 z-20 touch-manipulation group-hover:opacity-100 group-active:opacity-100 hover:bg-white active:bg-white sm:p-3"
              aria-label={tCarousel("next")}
            >
              {currentLocale.isRtl ? (
                <ChevronLeft className="h-5 w-5 text-gray-800 sm:h-6 sm:w-6" />
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-800 sm:h-6 sm:w-6" />
              )}
            </button>
          </>
        )}
      </div>
      
      {/* Dots indicator */}
      {showDots && validImages.length > 1 && (
        <div className="flex justify-center gap-3 mt-4 sm:mt-5">
          {validImages.map((_, index) => (
            <button
              type="button"
              key={index}
              onClick={() => goToImage(index)}
              className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-300 cursor-pointer touch-manipulation border-2 ${
                index === currentImageIndex 
                  ? 'bg-teal-600 border-teal-600 scale-110' 
                  : 'bg-white border-gray-400 hover:border-teal-400'
              }`}
              aria-label={`${tCarousel("next_short")} ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default DynamicCarousel
